<!-- Copyright (c) 2023, Alix Mougenot (Au pied des Arbres), alix.mougenot@gmail.com -->
<!-- See license file. -->
<script setup lang="ts">
import type { Ref } from "vue";
import type { Case, CaseCreation, CaseEdit } from "@/types";
import {
  signalPathName,
  complaintsPathName,
  infoPathName,
  differentPathName,
} from "@/router";

import { ref, computed, watch, inject } from "vue";
import { makeSearchEngine } from "@/logic/search";
import DossierDisplay from "@/components/dossier/DossierDisplay.vue";
import FormInput from "../FormInput.vue";
import { MDBSwitch } from "mdb-vue-ui-kit";
import { createCaseKey, filesKey, mutateCaseKey } from "@/logic/provide";
import {
  isCaseTypeDemande,
  isCaseTypeDifferent,
  isCaseTypePlainte,
  isCaseTypeSignalement,
} from "@/types";
import { useRoute } from "vue-router";
import { useLoginStore } from "@/stores/login";
import Navigation from "../navigation/Navigation.vue";
import DossierContent from "./DossierContent.vue";

export type CaseFilter = {
  myFiles: boolean;
  openFiles: boolean;
  medicalCenter: boolean;
  fileType: "Plainte" | "Demande" | "Signalement" | "Différend" | null;
};

const props = defineProps<{
  allowCreation: boolean;
  creationTemplate?: CaseEdit;
}>();

const route = useRoute();
const cases = inject(filesKey);
const editCase = inject(mutateCaseKey);
const createCase = inject(createCaseKey);

const userStore = useLoginStore();
const currentUser = computed(() => userStore.currentUser);
const creationFromTemplate = ref(props.creationTemplate);

// Watch the routes param to auto set a search filter
const searchInput = ref("");
const searchFilter: Ref<CaseFilter> = ref({
  myFiles: false,
  openFiles: false,
  medicalCenter: false,
  fileType: null,
});

function updateSearchFromLink() {
  if (route.params.id) {
    const id = route.params.id as string;
    searchInput.value = id ? id : "";
    searchFilter.value.myFiles = false;
    searchFilter.value.openFiles = false;
    searchFilter.value.medicalCenter = false;
  } else {
    searchInput.value = "";
    searchFilter.value.myFiles = true;
    searchFilter.value.openFiles = true;
    searchFilter.value.medicalCenter = false;
  }

  if (route.name == infoPathName) {
    searchFilter.value.fileType = "Demande";
  } else if (route.name == complaintsPathName) {
    searchFilter.value.fileType = "Plainte";
  } else if (route.name == signalPathName) {
    searchFilter.value.fileType = "Signalement";
  } else if (route.name == differentPathName) {
    searchFilter.value.fileType = "Différend";
  } else {
    searchFilter.value.fileType = null;
  }
}
watch(route, updateSearchFromLink, { immediate: true, deep: true });

const hintTitle = computed(() => {
  if (searchFilter.value.fileType == "Plainte") {
    return "Vos plaintes non-classées";
  } else if (searchFilter.value.fileType == "Signalement") {
    return "Vos signalements non-classés";
  } else if (searchFilter.value.fileType == "Demande") {
    return "Vos demandes non-classées";
  } else if (searchFilter.value.fileType == "Différend") {
    return "Vos différends non-classés";
  } else {
    return "Vos dossiers non-classés";
  }
});

// Handles selected case
const panelXInitialValue = 54;
const _selected = ref(null) as Ref<{
  caseID: string;
} | null>;
const panelX = ref(panelXInitialValue); // vertical placement of the event panel in the view

function caseSelected(selected: Case, offset: number) {
  if (window.getSelection()?.toString()) {
    // avoids false click when selecting a value to copy paste.
    return;
  }

  if (isCreatingCase.value) {
    return;
  }

  if (_selected.value && _selected.value.caseID === selected.id) {
    _selected.value = null;
  } else {
    _selected.value = {
      caseID: selected.id,
    };
  }
  panelX.value = offset + 24 + 15; // values are offset and height of the file counter section
}

function deselect() {
  if (_selected.value) {
    _selected.value = null;
  }
}

const currentSelection = computed(() => {
  if (isCreatingCase.value) {
    return caseTemplate.value;
  } else if (_selected && _selected.value) {
    const cId = cases?.value?.index.get(_selected.value.caseID);
    return cId !== undefined ? cases?.value?.list[cId] : undefined;
  }
  return undefined;
});

function onSelectionEdit(patch: CaseEdit) {
  if (currentSelection.value && currentSelection.value.id) {
    if (editCase) {
      if (Object.keys(patch).length !== 0) {
        editCase(currentSelection.value.id, patch);
      }
    } else {
      console.error("CaseList: editCase is not defined");
    }
  } else if (currentSelection.value && isCreatingCase.value) {
    caseTemplate.value = {
      ...caseTemplate.value,
      ...patch,
    };
  } else {
    console.error("CaseList: No case selected");
  }
}

// Case file creation

const isCreatingCase = ref(false);
const emptyCase: Case = {
  id: "",
  description: "",
  member_id: currentUser.value?.id || "",
  case_type: "Plainte",
  case_status: "Actif",
  case_concerns_center: false,
  sources: [],
  targets: [],
  creation_date: new Date().toISOString(),
  last_update: new Date().toISOString(),
};

const caseTemplate: Ref<Case> = ref({ ...emptyCase });

function onBeginCaseCreation() {
  _selected.value = null;
  panelX.value = panelXInitialValue;
  isCreatingCase.value = true;

  if (creationFromTemplate.value) {
    caseTemplate.value = {
      ...emptyCase,
      ...props.creationTemplate,
      member_id: currentUser.value?.id || "",
    };
    creationFromTemplate.value = undefined;
  }
}

function onCancelCaseCreation() {
  isCreatingCase.value = false;
  caseTemplate.value = { ...emptyCase };
}

async function onCreateCase(caseCreation: CaseCreation) {
  if (createCase) {
    const result = await createCase(caseCreation);
    console.log(result);
    const id = result?.data?.addCase;
    if (!id) {
      console.error("No id returned from createCase");
      return;
    } else {
      _selected.value = {
        caseID: id,
      };
    }
  } else {
    console.error("No createCase function provided");
    return;
  }
  isCreatingCase.value = false;
}

watch(
  props,
  () => {
    creationFromTemplate.value = props.creationTemplate;
    if (isCreatingCase.value) {
      onCancelCaseCreation(); // Reset the case creation when reloading
    } else if (props.creationTemplate) {
      console.log("Starting case creation from template");
      onBeginCaseCreation(); // Start the case creation from the template when present
    } else {
      // Reset the case template when reloading
      caseTemplate.value = {
        ...emptyCase,
        member_id: currentUser.value?.id || "",
      };
    }
  },
  { immediate: true }
);

// Hanlde filtering
const caseSource = computed(() => {
  if (searchInput.value.length > 1) {
    if (searchInput.value.startsWith("file:")) {
      const index = cases?.value?.index.get(searchInput.value);
      const found = index !== undefined ? cases?.value?.list[index] : undefined;
      return found ? [found] : [];
    } else {
      return searchResult.value as Array<Case>;
    }
  } else {
    return cases?.value.list || [];
  }
});

watch(
  caseSource,
  () => {
    // Auto select if only one case
    if (caseSource.value.length == 1) {
      _selected.value = {
        caseID: caseSource.value[0].id,
      };
    }
  },
  { immediate: true }
);

const filteredCases = computed(() => {
  return caseSource.value.filter(
    (c: Case) =>
      (c.case_status !== "Classé" || !searchFilter.value.openFiles) &&
      ((c.member_id !== null &&
        (c.member_id == currentUser.value?.id ||
          c.member_id == currentUser.value?.legacyID)) ||
        !searchFilter.value.myFiles) &&
      (!searchFilter.value.medicalCenter ||
        c.case_concerns_center ||
        c.targets.find((a) => a.type === "cntr")) && // A => B (not A or B)
      (!(searchFilter.value.fileType == "Plainte") ||
        isCaseTypePlainte(c.case_type)) &&
      (!(searchFilter.value.fileType == "Signalement") ||
        isCaseTypeSignalement(c.case_type)) &&
      (!(searchFilter.value.fileType == "Demande") ||
        isCaseTypeDemande(c.case_type)) &&
      (!(searchFilter.value.fileType == "Différend") ||
        isCaseTypeDifferent(c.case_type))
  );
});

const currentPage: Ref<number> = ref(0);
const loadMoreCases = () => {
  currentPage.value += 1;
};
const currentCases = computed(() =>
  filteredCases.value.slice(0, (currentPage.value + 1) * 20)
);
const maxPage = computed(() => Math.ceil(filteredCases.value.length / 20) - 1);

watch(currentCases, () => {
  if (_selected.value) {
    if (
      currentCases.value.find((c) => c.id == _selected.value?.caseID) ===
      undefined
    ) {
      deselect();
    }
  }
});

// search
function onFilterChange() {
  deselect();
  currentPage.value = 0;
}
watch([searchFilter, searchInput], onFilterChange, { deep: true });
const searchSource = computed(() => cases?.value.list || []);
const { searchResult } = makeSearchEngine(
  {
    includeScore: true,
    ignoreLocation: true,
    findAllMatches: true,
    minMatchCharLength: 3,
    keys: [
      { name: "sources", weight: 2 },
      { name: "targets", weight: 2 },
      { name: "id" },
      "description",
    ],
  },
  searchInput,
  searchSource
);
</script>

<template>
  <!-- SEARCH HEADER -->
  <Teleport to="#toolbar">
    <span>Recherche dans :</span>
    <div>
      <MDBSwitch v-model="searchFilter.myFiles" label="Mes dossiers" />
    </div>
    <div>
      <MDBSwitch v-model="searchFilter.openFiles" label="Dossiers ouverts" />
    </div>
    <div>
      <MDBSwitch
        v-model="searchFilter.medicalCenter"
        label="Dossiers centres dentaires"
      />
    </div>
  </Teleport>
  <Teleport to="#searchbar">
    <FormInput v-model="searchInput" label="Recherche" type="text" />
  </Teleport>
  <!-- MAIN LISTING -->
  <div class="dossiers">
    <div class="container-cases">
      <div class="container-stats">
        <span>{{ filteredCases.length }}</span>
        <span v-if="filteredCases.length <= 1"> dossier </span>
        <span v-else> dossiers </span>
        <template v-if="maxPage > 0">
          (<span>{{ currentPage + 1 }}</span>
          {{ currentPage == 0 ? "page" : "pages" }} /
          <span>{{ maxPage + 1 }}</span
          >)
        </template>
      </div>
      <div class="container-cases-listing">
        <div v-if="currentCases.length === 0 && !isCreatingCase">
          <h3>{{ hintTitle }}</h3>
          <span
            >Tant qu'ils ne sont pas classés, les dossiers dont vous avez la
            charge apparaissent ici.
          </span>
          <br />
          <span
            >Vous pouvez changer le conseiller en charge d'un dossier en
            utilisant le bouton éditer. Les filtres en haut vous permettent
            d'acceder aux autres dossiers.</span
          >
          <br />
          <span> </span>
        </div>
        <div
          v-if="!isCreatingCase && props.allowCreation"
          @click="onBeginCaseCreation"
        >
          <span class="button"> Créer un nouveau dossier</span>
        </div>
        <div v-if="isCreatingCase">
          <DossierDisplay
            :modelValue="caseTemplate"
            openMode="Create"
            @click:create="onCreateCase"
            @click:cancel="onCancelCaseCreation"
          />
        </div>
        <template v-for="dossier of currentCases" :key="dossier.id">
          <DossierDisplay
            :class="{
              hide: currentSelection && currentSelection.id !== dossier.id,
              selected: currentSelection && currentSelection.id === dossier.id,
              list: true,
            }"
            openMode="Edit"
            :modelValue="dossier"
            @click:detail="caseSelected"
          />
        </template>
        <div v-if="currentPage < maxPage" class="center">
          <span class="button" @click="loadMoreCases">Montrer plus</span>
        </div>
      </div>
    </div>
    <Navigation navKey="Détail du dossier">
      <DossierContent
        v-if="currentSelection"
        :case="currentSelection"
        :panelX="panelX"
        @update:case="onSelectionEdit"
      />
    </Navigation>
  </div>
</template>

<style scoped lang="scss">
.dossiers {
  display: flex;
  flex-direction: row;
  column-gap: 3em;

  .container-cases {
    display: flex;
    flex-direction: column;
    padding-top: 1em;
    padding-left: 1em;
    margin-right: 1em; // compensates card when animated during selection

    .container-stats {
      text-align: right;
    }
    .container-cases-listing {
      display: flex;
      flex-direction: column;
      padding-top: 1em;
      row-gap: 4em;

      margin-bottom: 100px;
    }
  }

  .center {
    align-items: center;
    display: flex;
    flex-direction: row;
    flex-wrap: nowrap;
    justify-content: space-evenly;
  }

  .hide {
    opacity: 0.2;
  }

  .listing {
    display: flex;
    flex-direction: column;
    padding-top: 2em;
    padding-bottom: 2em;
    margin-left: 1em;
    row-gap: 2em;
  }
}
</style>
